"use client";

import React from "react";
import { CircleAlert as AlertCircle, FileText, HeartPulse, Info, ShieldAlert, Stethoscope, Activity, Wind, Pill } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
interface DisclosureItem {
  title: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
}
const disclosureItems: DisclosureItem[] = [{
  title: "Relevant cardiovascular conditions",
  icon: HeartPulse
}, {
  title: "Respiratory conditions",
  icon: Wind
}, {
  title: "Significant current illness",
  icon: Activity
}, {
  title: "Recent surgery",
  icon: Stethoscope
}, {
  title: "Chest pain, unexplained fainting or similar concerning symptoms",
  icon: AlertCircle
}, {
  title: "Medications or conditions that may affect exercise response",
  icon: Pill
}];
export default function TestingSuitability() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.15
  });
  return <section id="testingsuitability" data-nav="light" ref={ref} className="bg-background text-foreground py-16 md:py-24 border-b border-border transition-opacity duration-700" style={{
    opacity: isVisible ? 1 : 0
  }}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Header */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground mb-6">
            Is VO₂ Max testing suitable for everyone?
          </h2>
          <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed">
            VO₂ testing involves progressively increasing exercise intensity and may not be appropriate for every person on every occasion.
          </p>
        </div>

        {/* Core Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Left Column: Readiness Process & Protocol Decisions */}
          <div className="lg:col-span-6 space-y-6">
            <Card className="border border-border shadow-xs bg-card">
              <CardHeader className="gap-2">
                <div className="flex items-center gap-2 text-primary font-medium text-sm">
                  <FileText className="size-4" />
                  <span>Pre-test health and readiness process</span>
                </div>
                <CardTitle className="text-xl sm:text-2xl font-semibold text-foreground">
                  Our clinical readiness screening
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-muted-foreground leading-relaxed text-sm sm:text-base">
                <p>
                  PhysAnalytica uses a structured pre-test health and readiness screening process prior to undertaking any physiological testing. This protocol allows us to evaluate individual exercise history and current physical readiness before administering maximal exertion protocols.
                </p>
                <div className="p-4 rounded-lg bg-muted/60 border border-border text-foreground">
                  <p className="font-medium text-sm sm:text-base leading-snug">
                    Testing may be modified, postponed or declined if information provided before or during the assessment indicates that maximal exercise is not appropriate. In some circumstances, medical clearance may be recommended before testing.
                  </p>
                </div>
              </CardContent>
            </Card>

            <Alert variant="default" className="border-border bg-card">
              <Info className="size-4 text-muted-foreground" />
              <AlertTitle className="text-foreground text-sm font-semibold">
                Clinical scope note
              </AlertTitle>
              <AlertDescription className="text-xs sm:text-sm text-muted-foreground mt-1 leading-relaxed">
                This assessment is designed for functional exercise physiology and biomechanics guidance. PhysAnalytica does not operate as a medical cardiology service, and completion of our health questionnaire does not prove or guarantee the absence of cardiovascular disease.
              </AlertDescription>
            </Alert>

            <div className="pt-2">
              <Button asChild size="lg" className="w-full sm:w-auto">
                <Link to="#">Book a pre-test consultation</Link>
              </Button>
            </div>
          </div>

          {/* Right Column: Required Disclosures List */}
          <div className="lg:col-span-6">
            <Card className="border border-border shadow-xs bg-card">
              <CardHeader className="gap-2 pb-4">
                <div className="flex items-center gap-2 text-primary font-medium text-sm">
                  <ShieldAlert className="size-4" />
                  <span>Safety disclosures</span>
                </div>
                <CardTitle className="text-xl sm:text-2xl font-semibold text-foreground">
                  Information you should disclose
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                  Please notify our team prior to testing if you have or experience any of the following:
                </p>
              </CardHeader>

              <CardContent className="space-y-3 pt-0">
                <ul className="divide-y divide-border" role="list">
                  {disclosureItems.map((item, index) => {
                  const IconComp = item.icon;
                  return <li key={item.title} data-index={index} className="py-3.5 first:pt-0 last:pb-0 flex items-start gap-3.5 text-sm sm:text-base">
                        <div className="p-2 rounded-md bg-secondary text-secondary-foreground shrink-0 mt-0.5">
                          <IconComp className="size-4 text-primary" />
                        </div>
                        <div className="flex-1 text-foreground font-medium self-center">
                          {item.title}
                        </div>
                      </li>;
                })}
                </ul>

                <div className="mt-6 pt-4 border-t border-border">
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    Note: This is an essential safety disclosure summary and is not presented as an exhaustive medical contraindication list. If you are uncertain about your suitability for maximal exercise, consult your physician.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}